import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Map, { Marker, FullscreenControl, Source, Layer, Popup } from "react-map-gl/mapbox";
import NorthOutlinedIcon from "@mui/icons-material/NorthOutlined";
import moment from "moment";
import "../timelineMap/timelineMap.scss";

const MAX_POINTS = 1000;

const TimelineMap = ({ timelinePoints }) => {
    const mapRef = useRef(null);
    const firstLoadRef = useRef(true);

    const [mapLoaded, setMapLoaded] = useState(false);
    const [popupSelected, setPopupSelected] = useState({});
    const [routeFeature, setRouteFeature] = useState(null);

    const popupEnterTimeout = useRef(null);
    const popupLeaveTimeout = useRef(null);

    const viewState = {
        latitude: 46.7795,
        longitude: 11.8816,
        zoom: 11,
    };

    // Punkte begrenzen (UI darf viele haben, Matching nicht)
    const limitedTimelinePoints = useMemo(
        () => (timelinePoints ?? []).slice(0, MAX_POINTS),
        [timelinePoints]
    );

    // FitBounds
    useEffect(() => {
        if (!mapLoaded || !mapRef.current || !limitedTimelinePoints.length) return;
        if (!firstLoadRef.current) return;

        const lats = limitedTimelinePoints.map(p => p.latitude);
        const lngs = limitedTimelinePoints.map(p => p.longitude);

        const bounds = [
            [Math.min(...lngs), Math.min(...lats)], // Südwesten (lng, lat)
            [Math.max(...lngs), Math.max(...lats)]  // Nordosten (lng, lat)
        ];

        mapRef.current.fitBounds(bounds, {
            padding: 60,
            maxZoom: 16,
            duration: 800,
        });
    }, [mapLoaded, limitedTimelinePoints]);

    // EIN Mapbox Request
    const getRoute = useCallback(async (points) => {
        if (!points || points.length < 2) {
            setRouteFeature(null);
            return;
        }

        // >100 Punkte → NUR roh verbinden
        if (points.length > 100) {
            setRouteFeature({
                type: "FeatureCollection",
                features: [{
                    type: "Feature",
                    geometry: {
                        type: "LineString",
                        coordinates: points.map(p => [p.longitude, p.latitude]),
                    },
                }],
            });
            return;
        }

        // ≤100 Punkte → Snapping
        const coords = points.map(p => `${p.longitude},${p.latitude}`).join(";");
        const radiuses = points.map(() => 100).join(";");

        try {
            const res = await fetch(
                `https://api.mapbox.com/matching/v5/mapbox/driving/${coords}` +
                `?geometries=geojson&radiuses=${radiuses}&tidy=true&access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`
            );
            const json = await res.json();

            if (json.code === "Ok" && json.matchings?.length) {
                setRouteFeature({
                    type: "FeatureCollection",
                    features: json.matchings.map(match => ({
                        type: "Feature",
                        geometry: match.geometry,
                    })),
                });
            } else {
                // Fallback → roh
                setRouteFeature({
                    type: "FeatureCollection",
                    features: [{
                        type: "Feature",
                        geometry: {
                            type: "LineString",
                            coordinates: points.map(p => [p.longitude, p.latitude]),
                        },
                    }],
                });
            }
        } catch {
            // Netzwerkfehler → roh
            setRouteFeature({
                type: "FeatureCollection",
                features: [{
                    type: "Feature",
                    geometry: {
                        type: "LineString",
                        coordinates: points.map(p => [p.longitude, p.latitude]),
                    },
                }],
            });
        }
    }, []);

    useEffect(() => {
        getRoute(limitedTimelinePoints);
    }, [limitedTimelinePoints, getRoute]);

    // Popup Handling
    const showPopup = (data) => {
        clearTimeout(popupLeaveTimeout.current);
        popupEnterTimeout.current = setTimeout(() => setPopupSelected(data), 0);
    };

    const hidePopup = () => {
        clearTimeout(popupEnterTimeout.current);
        popupLeaveTimeout.current = setTimeout(() => setPopupSelected({}), 200);
    };

    return (
        <div className="timelineMap">
            <Map
                ref={mapRef}
                initialViewState={viewState}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                style={{ width: "100%", height: "100%" }}
                bearing={0}
                maxPitch={0}
                onLoad={() => setMapLoaded(true)}
            >
                <FullscreenControl position="top-right" />

                {limitedTimelinePoints.map((point, index) => (
                    <Marker key={index} longitude={point.longitude} latitude={point.latitude}>
                        <div
                            className={point.ignition ? "mark on" : "mark off"}
                            style={{ transform: `rotate(${point.angle}deg)` }}
                            onMouseEnter={() => showPopup({ timestamp: point.timestamp })}
                            onMouseLeave={hidePopup}
                        >
                            <NorthOutlinedIcon />
                        </div>

                        {popupSelected.timestamp === point.timestamp && (
                            <Popup
                                longitude={point.longitude}
                                latitude={point.latitude}
                                closeOnClick={false}
                                onClose={() => setPopupSelected({})}
                            >
                                {moment.parseZone(point.timestamp).local().format("HH:mm")} Uhr
                            </Popup>
                        )}
                    </Marker>
                ))}

                {routeFeature && (
                    <Source id="timeline-line" type="geojson" data={routeFeature}>
                        <Layer
                            id="lineLayer"
                            type="line"
                            paint={{
                                "line-color": "#000",
                                "line-width": 3,
                                "line-opacity": 0.85,
                            }}
                        />
                    </Source>
                )}
            </Map>
        </div>
    );
};

export default TimelineMap;